// src/utils/blogMarkdown.ts
// Tiny, dependency-free parser for the lightweight markdown used in blog post bodies.
import { BlogBlock, BlogInline } from '../types/blogTypes';

const INLINE_PATTERN = /(\*\*[^*]+\*\*|\*[^*]+\*|\[[^\]]+\]\([^)]+\))/g;
const LINK_PATTERN = /^\[([^\]]+)\]\(([^)]+)\)$/;
const SAFE_HREF_PATTERN = /^(\/|https:\/\/)/;

/** Splits a paragraph into text / bold / italic / link pieces. Unsafe link targets render as plain text. */
export const parseInline = (text: string): BlogInline[] =>
  text
    .split(INLINE_PATTERN)
    .filter((part) => part.length > 0)
    .map((part): BlogInline => {
      if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
        return { type: 'bold', text: part.slice(2, -2) };
      }
      if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
        return { type: 'italic', text: part.slice(1, -1) };
      }
      const link = LINK_PATTERN.exec(part);
      if (link) {
        return SAFE_HREF_PATTERN.test(link[2])
          ? { type: 'link', text: link[1], href: link[2] }
          : { type: 'text', text: link[1] };
      }
      return { type: 'text', text: part };
    });

const isBullet = (line: string): boolean => /^- /.test(line);
const isOrdered = (line: string): boolean => /^\d+\. /.test(line);

/** Converts a post body into renderable blocks. Blocks are separated by blank lines. */
export const parseBlogBody = (body: string): BlogBlock[] =>
  body
    .trim()
    .split(/\n\s*\n/)
    .map((chunk) => chunk.trim())
    .filter((chunk) => chunk.length > 0)
    .map((chunk): BlogBlock => {
      const lines = chunk.split('\n').map((line) => line.trim());
      if (chunk.startsWith('### ')) return { type: 'h3', text: chunk.slice(4).trim() };
      if (chunk.startsWith('## ')) return { type: 'h2', text: chunk.slice(3).trim() };
      if (lines.every((line) => line.startsWith('>'))) {
        return { type: 'quote', text: lines.map((line) => line.replace(/^>\s?/, '')).join(' ') };
      }
      if (lines.every(isBullet)) return { type: 'ul', items: lines.map((l) => l.slice(2)) };
      if (lines.every(isOrdered)) {
        return { type: 'ol', items: lines.map((l) => l.replace(/^\d+\. /, '')) };
      }
      return { type: 'p', text: lines.join(' ') };
    });

export const countWords = (body: string): number =>
  body
    .replace(/[*>#\-[\]()]/g, ' ')
    .split(/\s+/)
    .filter((word) => word.length > 0).length;

const WORDS_PER_MINUTE = 220;

export const estimateReadingMinutes = (body: string): number =>
  Math.max(1, Math.round(countWords(body) / WORDS_PER_MINUTE));
